/**
 * HorizontalView Component
 * Horizontal calendar grid showing resources as rows and time as columns
 * Used for Day (1h), 2 Days (1h), 3 Days (2h), and 7 Days (4h) views
 */

import React, { useRef, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Spin, Empty, Tooltip, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { CalendarEvent, Resource } from '@/api/types/calendars.types';
import { useSunTimes, useWeatherForecast } from '@/hooks/useCalendars';
import { ResourceRow } from './ResourceRow';
import {
  getActiveResources,
  getEventsForResource,
} from './utils/resourceGrouping';
import './HorizontalView.css';

interface HorizontalViewProps {
  /** Current date (start date of the period) */
  currentDate: Date;
  /** Events to display */
  events: CalendarEvent[];
  /** Number of days to display */
  numDays: 1 | 2 | 3 | 7;
  /** Time resolution in hours (columns per this many hours) */
  hourResolution: 1 | 2 | 4;
  /** Loading state */
  loading?: boolean;
  /** Callback when event is clicked */
  onEventClick?: (event: CalendarEvent) => void;
  /** Callback when time slot is clicked */
  onSlotClick?: (date: Date, resourceId: string, hour: number) => void;
  /** Callback when event is updated via drag & drop */
  onEventUpdate?: (
    eventId: string,
    newStartTime: string,
    newEndTime: string
  ) => Promise<void>;
  /** Callback when date changes via navigation arrows (for Day 1h view) */
  onDateChange?: (date: Date) => void;
  /** Selected resource IDs (for filtering) */
  selectedResourceIds?: string[];
  /** All available resources */
  allResources?: Resource[];
  /** Airport ID for sun times (optional) */
  airportId?: string;
  /** Whether to show sunrise/sunset overlay */
  showDayNight?: boolean;
  /** Whether to show weather forecast */
  showWeather?: boolean;
  /** Weather coordinates (latitude/longitude) */
  weatherCoordinates?: {
    latitude: number | undefined;
    longitude: number | undefined;
    airportName: string;
  } | null;
  /** Selected airports with timezone info for multi-timezone display */
  selectedAirports?: { id: string; name: string; timezone?: string }[];
}

export const HorizontalView: React.FC<HorizontalViewProps> = ({
  currentDate,
  events,
  numDays,
  hourResolution,
  loading = false,
  onEventClick,
  onSlotClick,
  onEventUpdate,
  onDateChange,
  selectedResourceIds,
  allResources = [],
  airportId,
  showDayNight,
  showWeather,
  weatherCoordinates,
  selectedAirports = [],
}) => {
  const { timezone } = useUserTimezone();

  // Calculate additional timezones to display (airports with different timezone than user)
  const additionalTimezones = useMemo(() => {
    return selectedAirports
      .filter((airport) => airport.timezone && airport.timezone !== timezone)
      .map((airport) => ({
        timezone: airport.timezone!,
        name: airport.name,
        abbr: dayjs.tz(dayjs(), airport.timezone!).format('z'),
      }));
  }, [selectedAirports, timezone]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Current time state (for "now" indicator line)
  // Use UTC time and convert to user's timezone (not local computer time)
  const [currentTime, setCurrentTime] = React.useState(() =>
    dayjs.utc().tz(timezone)
  );

  // Drag & drop state
  const [draggedEvent, setDraggedEvent] = React.useState<CalendarEvent | null>(
    null
  );
  const [dragOffsetX, setDragOffsetX] = React.useState<number>(0);
  const [dropPreview, setDropPreview] = React.useState<{
    left: number;
    width: number;
    hasConflict: boolean;
  } | null>(null);

  // Get days to display
  const days = useMemo(() => {
    const result = [];
    const start = dayjs.tz(currentDate, timezone).startOf('day');
    for (let i = 0; i < numDays; i++) {
      result.push(start.add(i, 'day'));
    }
    return result;
  }, [currentDate, numDays, timezone]);

  // Generate time columns based on hour resolution
  const timeColumns = useMemo(() => {
    const columns: { label: string; day: dayjs.Dayjs; hour: number }[] = [];
    const hoursPerDay = 24;

    days.forEach((day) => {
      for (let hour = 0; hour < hoursPerDay; hour += hourResolution) {
        columns.push({
          label: `${hour.toString().padStart(2, '0')}:00`,
          day,
          hour,
        });
      }
    });

    return columns;
  }, [days, hourResolution]);

  // Get active resources (those with events in this period)
  const activeResources = useMemo(
    () => getActiveResources(events, allResources, selectedResourceIds),
    [events, allResources, selectedResourceIds]
  );

  // Calculate position of "now" indicator line
  const currentTimePosition = useMemo(() => {
    // Check if current time is within the displayed date range
    const firstDay = days[0];
    const lastDay = days[days.length - 1];
    if (!firstDay || !lastDay) return null;

    const startOfPeriod = firstDay.startOf('day');
    const endOfPeriod = lastDay.endOf('day');

    // If current time is outside the displayed period, don't show the line
    if (
      currentTime.isBefore(startOfPeriod) ||
      currentTime.isAfter(endOfPeriod)
    ) {
      return null;
    }

    // Calculate minutes from start of period (midnight)
    const minutesFromStart = currentTime.diff(startOfPeriod, 'minute');

    // Calculate pixel position
    // Each hour column is 120px wide (from CSS)
    // Need to add 200px for the sticky label column on the left
    const columnWidth = 120;
    const minutesPerColumn = hourResolution * 60;
    const pixelsPerMinute = columnWidth / minutesPerColumn;
    const stickyLabelWidth = 200; // From CSS .resource-row-label width
    const leftPosition = stickyLabelWidth + minutesFromStart * pixelsPerMinute;

    return {
      position: leftPosition,
      time: currentTime.format('HH:mm'),
      date: currentTime.format('dddd, D. MMMM YYYY'),
    };
  }, [currentTime, days, hourResolution]);

  // Fetch weather forecast for the location
  const { data: weatherData } = useWeatherForecast(
    weatherCoordinates?.latitude,
    weatherCoordinates?.longitude,
    showWeather && !!weatherCoordinates
  );

  // Fetch sun times for the period
  const { data: sunTimesData } = useSunTimes(
    airportId || '',
    {
      date_from: days[0]?.format('YYYY-MM-DD'),
      date_to: days[days.length - 1]?.format('YYYY-MM-DD'),
    },
    showDayNight && !!airportId && days.length > 0
  );

  // Create a map for quick lookup by date
  const sunTimesMap = useMemo(() => {
    if (!sunTimesData) return {};

    // Handle range response
    if ('items' in sunTimesData) {
      return Object.fromEntries(
        sunTimesData.items.map((item) => [item.date, item])
      );
    }

    // Handle single date response
    return { [sunTimesData.date]: sunTimesData };
  }, [sunTimesData]);

  // Update current time every minute
  useEffect(() => {
    const updateCurrentTime = () => {
      // Use UTC time and convert to user's timezone (works regardless of computer's local time)
      setCurrentTime(dayjs.utc().tz(timezone));
    };

    // Update immediately
    updateCurrentTime();

    // Update every minute
    const interval = setInterval(updateCurrentTime, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [timezone]);

  // Auto-scroll to 8 AM on mount
  useEffect(() => {
    if (gridRef.current && !loading) {
      // Find column index for 8:00 AM
      const eightAmIndex = timeColumns.findIndex((col) => col.hour === 8);
      if (eightAmIndex !== -1) {
        const columnWidth = 120; // From CSS
        gridRef.current.scrollLeft = eightAmIndex * columnWidth;
      }
    }
  }, [loading, currentDate, timeColumns]);

  // Helper: Check if dropping at this time would create a resource conflict
  const checkResourceConflict = (
    newStartTime: dayjs.Dayjs,
    newEndTime: dayjs.Dayjs
  ): boolean => {
    if (!draggedEvent) return false;

    // Get resource IDs from the dragged event, EXCLUDING AIRPORT type
    // Airports can have multiple simultaneous operations (different planes/instructors)
    const draggedResourceIds = draggedEvent.resources
      .filter((r) => r.resource_type !== 'AIRPORT')
      .map((r) => r.resource_id);

    // If no constraining resources (only airport), no conflict possible
    if (draggedResourceIds.length === 0) return false;

    // Check if any other event uses the same resources in overlapping time
    return events.some((event) => {
      // Skip the event being dragged
      if (event.id === draggedEvent.id) return false;

      // Check if this event shares any non-airport resources with dragged event
      const hasSharedResource = event.resources.some(
        (r) =>
          r.resource_type !== 'AIRPORT' &&
          draggedResourceIds.includes(r.resource_id)
      );

      if (!hasSharedResource) return false;

      // Check time overlap
      const eventStart = dayjs(event.start_time);
      const eventEnd = dayjs(event.end_time);

      // Events overlap if one starts before the other ends
      return newStartTime.isBefore(eventEnd) && newEndTime.isAfter(eventStart);
    });
  };

  // Drag & drop handlers
  const handleEventDragStart = (
    event: CalendarEvent,
    _resourceId: string,
    e: React.DragEvent
  ) => {
    if (!onEventUpdate) return;

    // Get the event element to calculate offset
    const eventElement = e.currentTarget as HTMLElement;
    const rect = eventElement.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;

    setDraggedEvent(event);
    setDragOffsetX(offsetX);
  };

  const handleDragOver = (_resourceId: string, e: React.DragEvent) => {
    if (!draggedEvent) return;
    e.preventDefault();

    // Get the resource row grid element
    const gridElement = e.currentTarget as HTMLElement;
    const rect = gridElement.getBoundingClientRect();
    const relativeX = e.clientX - rect.left - dragOffsetX;

    // Snap to 15-minute intervals
    const columnWidth = 120;
    const minutesPerColumn = hourResolution * 60;
    const pixelsPerMinute = columnWidth / minutesPerColumn;
    const minutesFromStart = relativeX / pixelsPerMinute;

    // Round to nearest 15-minute interval
    const snappedMinutes = Math.round(minutesFromStart / 15) * 15;
    const snappedLeft = snappedMinutes * pixelsPerMinute;

    // Calculate event duration in minutes
    const eventStart = dayjs(draggedEvent.start_time);
    const eventEnd = dayjs(draggedEvent.end_time);
    const durationMinutes = eventEnd.diff(eventStart, 'minutes');
    const eventWidth = durationMinutes * pixelsPerMinute;

    // Calculate new start/end times to check for conflicts
    const periodStart = days[0];
    const newStartTime = periodStart.add(snappedMinutes, 'minutes');
    const newEndTime = newStartTime.add(durationMinutes, 'minutes');

    // Check for resource conflicts
    const hasConflict = checkResourceConflict(newStartTime, newEndTime);

    setDropPreview({
      left: Math.max(0, snappedLeft),
      width: eventWidth,
      hasConflict,
    });
  };

  const handleDragLeave = () => {
    setDropPreview(null);
  };

  const handleEventDrop = async (_resourceId: string, e: React.DragEvent) => {
    if (!draggedEvent || !onEventUpdate) return;

    // Clear preview immediately
    setDropPreview(null);

    try {
      // Use same calculation as handleDragOver (15-minute snap)
      const gridElement = e.currentTarget as HTMLElement;
      const rect = gridElement.getBoundingClientRect();
      const relativeX = e.clientX - rect.left - dragOffsetX;

      // Snap to 15-minute intervals
      const columnWidth = 120;
      const minutesPerColumn = hourResolution * 60;
      const pixelsPerMinute = columnWidth / minutesPerColumn;
      const minutesFromStart = relativeX / pixelsPerMinute;

      // Round to nearest 15-minute interval
      const snappedMinutes = Math.round(minutesFromStart / 15) * 15;

      // Calculate new start time
      const periodStart = days[0];
      const newStartTime = periodStart.add(snappedMinutes, 'minutes');

      // Preserve event duration
      const originalStart = dayjs(draggedEvent.start_time);
      const originalEnd = dayjs(draggedEvent.end_time);
      const durationMinutes = originalEnd.diff(originalStart, 'minutes');
      const newEndTime = newStartTime.add(durationMinutes, 'minutes');

      // Check for resource conflicts - PREVENT drop if conflict exists
      const hasConflict = checkResourceConflict(newStartTime, newEndTime);
      if (hasConflict) {
        console.warn('Cannot drop: Resource conflict detected');
        return; // Abort drop
      }

      // Convert to ISO strings (backend expects UTC)
      const newStartISO = newStartTime.toISOString();
      const newEndISO = newEndTime.toISOString();

      // Call update callback
      await onEventUpdate(draggedEvent.id, newStartISO, newEndISO);
    } catch (error) {
      console.error('Failed to update event:', error);
    } finally {
      setDraggedEvent(null);
    }
  };

  if (loading) {
    return (
      <div className="horizontal-view-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (activeResources.length === 0) {
    return (
      <div className="horizontal-view-empty">
        <Empty
          description="Nema resursa sa događajima u ovom periodu"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Navigation handlers for Day (1h) view
  const handlePreviousDay = () => {
    if (onDateChange && numDays === 1) {
      const newDate = dayjs(currentDate).subtract(1, 'day').toDate();
      onDateChange(newDate);
    }
  };

  const handleNextDay = () => {
    if (onDateChange && numDays === 1) {
      const newDate = dayjs(currentDate).add(1, 'day').toDate();
      onDateChange(newDate);
    }
  };

  // Calculate total width for horizontal scroll
  const numColumns = numDays * (24 / hourResolution);
  const resourceWidth = 200; // pixels
  const columnWidth = 120; // pixels per time column
  const totalWidth = resourceWidth + numColumns * columnWidth;

  return (
    <div className="horizontal-view">
      {/* Day header with navigation for single day view */}
      {numDays === 1 && onDateChange && (
        <div className="horizontal-single-day-header">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={handlePreviousDay}
            className="horizontal-nav-button"
          />
          <span className="horizontal-day-title">
            {days[0].format('dddd, D. MMMM YYYY')}
          </span>
          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={handleNextDay}
            className="horizontal-nav-button"
          />
        </div>
      )}

      {/* Scroll container for headers and content */}
      <div className="horizontal-view-scroll-container" ref={gridRef}>
        {/* Header with day labels (only if multiple days) */}
        {numDays > 1 && (
          <div
            className="horizontal-view-day-header"
            style={{ width: `${totalWidth}px`, minWidth: `${totalWidth}px` }}
          >
            <div className="horizontal-resource-label">Resources</div>
            {days.map((day, index) => {
              const columnsForDay = 24 / hourResolution;
              const dayWidth = columnsForDay * columnWidth;

              // Get weather forecast for this day
              const dayForecast =
                showWeather && weatherData
                  ? weatherData.forecasts.find(
                      (f: any) => f.date === day.format('YYYY-MM-DD')
                    )
                  : null;

              return (
                <div
                  key={index}
                  className="horizontal-day-label"
                  style={{ width: `${dayWidth}px`, minWidth: `${dayWidth}px` }}
                >
                  <div className="horizontal-day-name">
                    {day.format('ddd, MMM D')}
                  </div>
                  {dayForecast && (
                    <Tooltip
                      title={
                        <div className="weather-tooltip-content">
                          <div className="weather-tooltip-description">
                            {dayForecast.weather.description}
                          </div>
                          <div className="weather-tooltip-details">
                            <div>Vlažnost: {dayForecast.humidity}%</div>
                            <div>Pritisak: {dayForecast.pressure} hPa</div>
                            <div>
                              Vetar: {dayForecast.wind_speed.toFixed(1)} m/s
                            </div>
                            <div>
                              Padavine: {Math.round(dayForecast.pop * 100)}%
                            </div>
                          </div>
                        </div>
                      }
                      placement="bottom"
                      color="rgba(0, 0, 0, 0.85)"
                    >
                      <span className="inline-weather">
                        <img
                          src={`https://openweathermap.org/img/wn/${dayForecast.weather.icon}.png`}
                          alt={dayForecast.weather.description}
                          className="inline-weather-icon"
                        />
                        <span className="inline-weather-temp">
                          {Math.round(dayForecast.temperature.max)}°
                        </span>
                      </span>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Time header */}
        <div
          className="horizontal-view-header"
          style={{
            width: `${totalWidth}px`,
            minWidth: `${totalWidth}px`,
            top: numDays > 1 ? '49px' : '0', // Offset by day header height
          }}
        >
          <div className="horizontal-resource-label">
            {numDays === 1 ? 'Resources' : ''}
          </div>
          {timeColumns.map((col, index) => (
            <div
              key={index}
              className={`horizontal-time-column-header ${additionalTimezones.length > 0 ? 'has-additional-tz' : ''}`}
            >
              <span className="time-primary">{col.label}</span>
              {additionalTimezones.map((tz) => {
                // Convert hour from user timezone to airport timezone
                const userTime = col.day.hour(col.hour).minute(0).second(0);
                const airportTime = userTime.tz(tz.timezone);
                const airportHour = airportTime.format('HH:mm');

                return (
                  <Tooltip
                    key={tz.timezone}
                    title={`${tz.name} (${tz.timezone})`}
                    placement="bottom"
                  >
                    <span className="time-secondary">
                      {airportHour} ({tz.abbr})
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>

        {/* Content with resources and events */}
        <div className="horizontal-view-content">
          {/* Sticky overlay to hide current time indicator behind resource labels */}
          <div className="horizontal-sticky-label-overlay" />

          {activeResources.map((resource, index) => {
            const resourceEvents = getEventsForResource(events, resource.id);

            // Check if this resource is part of the dragged event
            const isResourceInDraggedEvent = draggedEvent?.resources?.some(
              (r) => r.resource_id === resource.id
            );

            // Check if we need to render a group separator before this resource
            const previousResource =
              index > 0 ? activeResources[index - 1] : null;
            const needsSeparator =
              previousResource &&
              previousResource.resource_type !== resource.resource_type;

            return (
              <React.Fragment key={resource.id}>
                {/* Group separator between different resource types */}
                {needsSeparator && (
                  <div className="resource-group-separator">
                    {/* Sticky label part */}
                    <div className="resource-group-separator-label" />
                    {/* Scrollable grid part */}
                    <div className="resource-group-separator-grid">
                      {timeColumns.map((_, colIndex) => (
                        <div
                          key={colIndex}
                          className="resource-group-separator-slot"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <ResourceRow
                  resource={resource}
                  events={resourceEvents}
                  days={days}
                  timeColumns={timeColumns}
                  hourResolution={hourResolution}
                  onEventClick={onEventClick}
                  onSlotClick={onSlotClick}
                  onEventDragStart={
                    onEventUpdate
                      ? (event, e) =>
                          handleEventDragStart(event, resource.id, e)
                      : undefined
                  }
                  onDragOver={
                    onEventUpdate
                      ? (e) => handleDragOver(resource.id, e)
                      : undefined
                  }
                  onDragLeave={onEventUpdate ? handleDragLeave : undefined}
                  onDrop={
                    onEventUpdate
                      ? (e) => handleEventDrop(resource.id, e)
                      : undefined
                  }
                  draggedEventId={draggedEvent?.id}
                  dropPreview={
                    dropPreview && isResourceInDraggedEvent ? dropPreview : null
                  }
                  sunTimesMap={sunTimesMap}
                  showDayNight={showDayNight}
                />
              </React.Fragment>
            );
          })}

          {/* Current time indicator line */}
          {currentTimePosition !== null && (
            <Tooltip
              title={
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {currentTimePosition.time}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.85 }}>
                    {currentTimePosition.date}
                  </div>
                </div>
              }
              placement="top"
            >
              <div
                className="horizontal-current-time-indicator"
                style={{ left: `${currentTimePosition.position}px` }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
